import { Clock, Train, Bus, Car } from "lucide-react";
import { TransportationActivity, ItineraryActivity, SupplierStatus } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { DocumentationList } from "@/components/ui/documentation-list";
import { ActivityCost, ActivityLink } from "./ActivityFields";

interface TransportationCardProps {
  activity: TransportationActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onSaveToLibrary?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId?: string) => void;
  onDragEnd?: () => void;
  isTraveler?: boolean;
  isAdmin?: boolean;
  unsaved?: boolean;
  showSupplierStatus?: boolean;
  onSupplierStatusChange?: (activity: ItineraryActivity, status: SupplierStatus | null) => void;
}

export const TransportationCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onSaveToLibrary,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd,
  isTraveler = true,
  isAdmin = false,
  unsaved = false,
  showSupplierStatus = false,
  onSupplierStatusChange,
}: TransportationCardProps) => {
  // Helper function to get transport icon
  const getTransportIcon = (transport_type: string) => {
    switch (transport_type) {
      case 'train': return <Train className="h-4 w-4" />;
      case 'bus': return <Bus className="h-4 w-4" />;
      case 'car': return <Car className="h-4 w-4" />;
      case 'taxi': return <Car className="h-4 w-4" />;
      case 'metro': return <Train className="h-4 w-4" />;
      default: return <Car className="h-4 w-4" />;
    }
  };

  const renderAlwaysVisible = () => (
    <>
      {activity.travels && activity.travels.length > 0 && (
        <div className="flex items-center gap-2">
          {getTransportIcon(activity.transport_type)}
          <span>{activity.travels[0].address} → {activity.travels[activity.travels.length - 1].address}</span>
        </div>
      )}
      {activity.travels && activity.travels.length > 0 && activity.travels[0].time && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{activity.travels[0].time}</span>
        </div>
      )}
      {activity.travels && activity.travels.length > 2 && (
        <div className="flex items-center gap-2">
          <span>{activity.travels.length - 2 === 1 ? `1 stop` : `${activity.travels.length - 2} stops`}</span>
        </div>
      )}
      <ActivityCost cost={activity.cost} isAdmin={isAdmin} isTraveler={isTraveler} />
    </>
  );

  const renderCollapsible = () => (
    <>
      {/* Company */}
      {activity.company && (
        <div className="mb-4">
          <span className="font-medium mb-1 block">Company</span>
          <p className="text-gray-600">{activity.company}</p>
        </div>
      )}

      {/* Travel Route */}
      {activity.travels && activity.travels.length > 0 && (
        <div className="mb-4">
          <span className="font-medium mb-1 block">Travel Route</span>
          <div className="space-y-2">
            {activity.travels.map((travel, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="text-gray-600">
                  <div className="font-medium">
                    {index === 0 ? 'From ' : index === activity.travels.length - 1 ? 'To ' : `Stop ${index} `}
                    <span className="font-normal">{travel.address}</span>
                  </div>
                  <div>{travel.date} at {travel.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documentation */}
      <DocumentationList documentation={activity.documentation || []} />

      {/* Link */}
      <ActivityLink link={activity.link} isAdmin={isAdmin} isTraveler={isTraveler} />
    </>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onSaveToLibrary={onSaveToLibrary}
      onDragStart={onDragStart}
      onDropBefore={onDropBefore}
      onDragEnd={onDragEnd}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
      isTraveler={isTraveler}
      isAdmin={isAdmin}
      unsaved={unsaved}
      showSupplierStatus={showSupplierStatus}
      onSupplierStatusChange={onSupplierStatusChange}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
